<script lang="ts">
  import { createEventDispatcher } from "svelte"

  const dispatch = createEventDispatcher()

  export let controlPanelOpen = false
  export let activeTab = "visual"

  // --- Props are received from the parent and bound to the inputs ---
  // No default values are needed here, as the parent provides them.
  export let uiBloomStrength: number
  export let uiBloomRadius: number
  export let uiBloomThreshold: number
  export let uiActivationSpeed: number
  export let uiPropagationChance: number
  export let uiDecayRate: number
  export let uiPulseIntensity: number
  export let uiSpontaneousRate: number
  export let uiBranchGrowthRate: number
  export let uiBranchPruningRate: number
  export let uiStrengthDecayRate: number
  export let uiEnableParticles: boolean
  export let uiEnableLogging: boolean
  export let uiEnableDendrites: boolean
  export let uiFogEnabled: boolean
  export let uiFogColor: string
  export let uiFogDensity: number
  export let aboutPanel: any

  // This reactive block "uses" the props to satisfy the linter.
  // This is valid, no-op block of code.
  $: {
    uiBloomStrength
    uiBranchGrowthRate
    uiBranchPruningRate
    uiStrengthDecayRate
    uiFogEnabled
    uiFogColor
    uiFogDensity
    uiEnableLogging
  }

  function manualTrigger() {
    dispatch("manualTrigger")
  }
</script>

<!-- Control panel -->
{#if controlPanelOpen}
  <div class="control-panel">
    <slot name="top"></slot>
    <!-- Tabs -->
    <div class="dendritic-tabs">
      <button
        class="dendritic-tab {activeTab === 'visual' ? 'active' : ''}"
        on:click={() => (activeTab = "visual")}>Visual</button
      >
      <button
        class="dendritic-tab {activeTab === 'animation' ? 'active' : ''}"
        on:click={() => (activeTab = "animation")}>Animation</button
      >
      <button
        class="dendritic-tab {activeTab === 'interaction' ? 'active' : ''}"
        on:click={() => (activeTab = "interaction")}>Interaction</button
      >
      <button
        class="dendritic-tab {activeTab === 'about' ? 'active' : ''}"
        on:click={() => (activeTab = "about")}>About</button
      >
    </div>

    <!-- Tab content -->
    <div class="tab-content">
      {#if activeTab === "visual"}
        <div class="tab-panel"></div>
        <slot></slot>
        <div class="control-row">
          <label for="bloom-radius">Bloom Radius</label>
          <input
            type="range"
            id="bloom-radius"
            min="0"
            max="2"
            step="0.1"
            bind:value={uiBloomRadius}
          />
          <span class="value-display">{uiBloomRadius.toFixed(2)}</span>
        </div>
        <div class="control-row">
          <label for="bloom-threshold">Bloom Threshold</label>
          <input
            type="range"
            id="bloom-threshold"
            min="0"
            max="1"
            step="0.01"
            bind:value={uiBloomThreshold}
          />
          <span class="value-display">{uiBloomThreshold.toFixed(2)}</span>
        </div>
        <div class="control-row">
          <label for="pulse-intensity">Pulse Intensity</label>
          <input
            type="range"
            id="pulse-intensity"
            min="0.5"
            max="4"
            step="0.1"
            bind:value={uiPulseIntensity}
          />
          <span class="value-display">{uiPulseIntensity.toFixed(1)}</span>
        </div>
        <div class="control-row">
          <label for="enable-particles">Enable Particle Bursts</label>
          <input
            type="checkbox"
            id="enable-particles"
            bind:checked={uiEnableParticles}
          />
        </div>
        <div class="control-row">
          <label for="enable-logging">Enable Debug Logging</label>
          <input
            type="checkbox"
            id="enable-logging"
            bind:checked={uiEnableLogging}
          />
        </div>
        <div class="control-row">
          <label for="enable-dendrites">Enable Dendrites</label>
          <input
            type="checkbox"
            id="enable-dendrites"
            bind:checked={uiEnableDendrites}
          />
        </div>
      {:else if activeTab === "animation"}
        <div class="tab-panel">
          <h3>Animation Settings</h3>
          <div class="control-row">
            <label for="activation-speed">Pulse Speed</label>
            <input
              type="range"
              id="activation-speed"
              min="0.001"
              max="0.05"
              step="0.001"
              bind:value={uiActivationSpeed}
            />
            <span class="value-display">{uiActivationSpeed.toFixed(4)}</span>
          </div>
          <div class="control-row">
            <label for="propagation-chance">Propagation Chance</label>
            <input
              type="range"
              id="propagation-chance"
              min="0"
              max="1"
              step="0.05"
              bind:value={uiPropagationChance}
            />
            <span class="value-display">{uiPropagationChance.toFixed(2)}</span>
          </div>
          <div class="control-row">
            <label for="decay-rate">Decay Rate</label>
            <input
              type="range"
              id="decay-rate"
              min="0.001"
              max="0.05"
              step="0.001"
              bind:value={uiDecayRate}
            />
            <span class="value-display">{uiDecayRate.toFixed(4)}</span>
          </div>
        </div>
      {:else if activeTab === "interaction"}
        <div class="tab-panel">
          <h3>Interaction Settings</h3>
          <div class="control-row">
            <label for="spontaneous-rate">Spontaneous Activity</label>
            <input
              type="range"
              id="spontaneous-rate"
              min="0"
              max="0.01"
              step="0.0001"
              bind:value={uiSpontaneousRate}
            />
            <span class="value-display">{uiSpontaneousRate.toFixed(4)}</span>
          </div>
          <div class="control-info">
            <p><strong>Click</strong> on neurons to trigger activations</p>
            <p><strong>Hover</strong> over neurons to see connections</p>
            <p>Activations propagate along dendritic branches</p>
            <button
              on:click={manualTrigger}
              style="margin-top: 10px; padding: 8px 16px; background: rgba(100, 150, 255, 0.2); border: 1px solid rgba(100, 150, 255, 0.5); border-radius: 6px; color: white; cursor: pointer;"
            >
              Trigger Random Activation
            </button>
          </div>
        </div>
      {:else if activeTab === "about"}
        <div class="tab-panel dendritic-about-panel">
          {@html aboutPanel?.description || ""}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .control-panel {
    position: fixed;
    bottom: 70px;
    right: 20px;
    width: 330px;
    height: 400px;
    background: rgba(15, 25, 55, 0.85);
    border: 1px solid rgba(100, 140, 230, 0.4);
    color: rgba(220, 230, 255, 0.9);
    border-radius: 8px;
    overflow: hidden;
    z-index: 1000;
    box-shadow: 0 4px 20px rgba(0, 10, 30, 0.5);
    animation: slideIn 0.3s ease;
    display: flex;
    flex-direction: column;
    backdrop-filter: blur(15px);
  }
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .dendritic-tabs {
    display: flex;
    border-bottom: 1px solid rgba(100, 140, 230, 0.3);
    flex-wrap: nowrap;
  }
  .dendritic-tabs button {
    flex: 1;
    background: transparent;
    color: rgba(180, 210, 255, 0.7);
    border: none;
    padding: 6px 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 10px;
    white-space: nowrap;
    min-width: auto;
  }
  .dendritic-tabs button:hover {
    background: rgba(70, 100, 180, 0.2);
    color: rgba(200, 230, 255, 0.9);
  }
  .dendritic-tabs button.active {
    background: rgba(70, 100, 180, 0.3);
    color: rgba(220, 240, 255, 1);
    border-bottom: 1px solid rgba(100, 160, 255, 0.8);
  }
  .tab-content {
    padding: 15px;
    max-height: calc(100% - 40px);
    overflow-y: auto;
    flex-grow: 1;
    display: block;
    background-color: rgba(15, 25, 55, 0.85);
    border-radius: 0 0 8px 8px;
  }
  .tab-panel {
    display: block;
    width: 100%;
    color: rgba(220, 230, 255, 0.9);
    padding: 12px;
  }
  .tab-panel h3 {
    margin-top: 0;
    margin-bottom: 12px;
    font-size: 16px;
    color: rgba(220, 240, 255, 0.9);
    font-weight: 500;
  }
  .control-row {
    display: flex;
    align-items: center;
    gap: 0.7em;
    margin-bottom: 12px;
    width: 100%;
    background-color: transparent;
    flex-wrap: wrap;
    box-sizing: border-box;
  }
  .control-row label {
    flex: 1 1 40%;
    min-width: 110px;
    font-size: 12px;
    color: rgba(200, 220, 255, 0.8);
    font-weight: 500;
  }
  .control-row input[type="range"] {
    flex: 2 1 120px;
    min-width: 90px;
    max-width: 100%;
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: rgba(100, 150, 255, 0.2);
    border-radius: 2px;
    outline: none;
  }
  .control-row input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: rgba(100, 150, 255, 0.9);
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .control-row input[type="range"]::-webkit-slider-thumb:hover {
    background: rgba(120, 170, 255, 1);
    transform: scale(1.1);
  }
  .value-display {
    min-width: 40px;
    text-align: right;
    font-size: 12px;
    color: rgba(100, 150, 255, 0.9);
    font-weight: 600;
    flex: 0 0 40px;
    flex: 0 0 18px;
    min-width: 18px;
    min-height: 18px;
    margin-left: 0.5em;
    margin-right: 0.5em;
    accent-color: #38bdf8;
  }
  .tab-panel p {
    margin: 0 0 8px 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
  }
  .tab-panel p:last-child {
    margin-bottom: 0;
  }
  .tab-panel strong {
    color: rgba(100, 150, 255, 0.9);
  }
  @media (max-width: 768px) {
    .control-panel {
      bottom: 60px;
      right: 10px;
      left: 10px;
      width: auto;
      max-width: none;
    }
    .dendritic-tabs button {
      font-size: 0.9em;
      padding: 0.4em 0.8em;
    }
    .tab-content {
      max-height: 250px;
    }
  }
</style>
